import { useEffect, useMemo, useState } from 'react';
import { PiArrowsDownUpDuotone, PiArrowUpDuotone } from 'react-icons/pi';

import { PopupLoader } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';

const ALIGN_CLASS_MAP = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
};

function normalizeSortValue(value) {
    // if (value === null || value === undefined || value === '') { return null; }
    if (value instanceof Date) { return value.getTime(); }
    if (typeof value === 'number' || typeof value === 'boolean') { return value; }

    if (typeof value === 'string') {
        const parsedDate = new Date(value);
        if (!Number.isNaN(parsedDate.getTime())) { return parsedDate.getTime(); }
        return value.toLowerCase();
    }

    return String(value).toLowerCase();
}

function compareValues(a, b) {
    if (a === b) { return 0; }
    if (a === null || a === undefined || a === '') { return 1; }
    if (b === null || b === undefined || b === '') { return -1; }

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base'
    });
}

function getInputAlignClass(column) {
    return ALIGN_CLASS_MAP[column.align || (column.numeric ? 'right' : 'left')] || ALIGN_CLASS_MAP.left;
}

export default function OptimizationPipelineInputTable(props) {
    const {
        columns = [],
        rows = [],
        errors = {},
        loading = false,
        error = null,
        onChange,
        className,
        minHeight = 320,
        rowKey = 'id',
        tableMinWidth = null,
        states = {},
        sort = {}
    } = props;

    const {
        key: defaultSortKey = null,
        direction: defaultSortDirection = 'asc'
    } = sort;

    const [activeSort, setActiveSort] = useState({
        key: defaultSortKey,
        direction: defaultSortDirection
    });

    useEffect(() => {
        setActiveSort({
            key: defaultSortKey,
            direction: defaultSortDirection
        });
    }, [defaultSortKey, defaultSortDirection]);

    const preparedRows = useMemo(() => {
        let nextRows = rows;

        if (activeSort?.key) {
            const sortColumn = columns.find((column) => column.key === activeSort.key);

            if (sortColumn?.sortable) {
                nextRows = [].concat(nextRows).sort((rowA, rowB) => {
                    const valueA = typeof sortColumn.getSortValue === 'function'
                        ? sortColumn.getSortValue(rowA)
                        : rowA?.[sortColumn.key];
                    const valueB = typeof sortColumn.getSortValue === 'function'
                        ? sortColumn.getSortValue(rowB)
                        : rowB?.[sortColumn.key];
                    const comparison = compareValues(normalizeSortValue(valueA), normalizeSortValue(valueB));

                    return activeSort.direction === 'desc' ? comparison * -1 : comparison;
                });
            }
        }

        return nextRows;
    }, [activeSort, columns, rows]);

    function handleSort(column) {
        if (!column.sortable) { return; }

        setActiveSort((prevState) => {
            if (prevState.key !== column.key) {
                return {
                    key: column.key,
                    direction: column.defaultSortDirection || 'asc'
                };
            }

            return {
                key: column.key,
                direction: prevState.direction === 'asc' ? 'desc' : 'asc'
            };
        });
    }

    const hasRows = rows.length > 0;
    const hasPreparedRows = preparedRows.length > 0;
    const hasHorizontalScroll = !!tableMinWidth;

    if (loading) {
        return (
            <div className={'w-full h-full min-h-0 flex flex-col p-16 ' + (className || '')}>
                <PopupLoader label={states.loadingTitle || 'Loading data...'} minHeight={minHeight} />
            </div>
        );
    }

    if (error) {
        return (
            <div className={'w-full h-full min-h-0 flex flex-col p-16 ' + (className || '')}>
                <div
                    className='w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium'
                    style={{ minHeight: minHeight }}
                >
                    {states.errorTitle || 'Something went wrong'}
                </div>
            </div>
        );
    }

    if (!hasRows) {
        return (
            <div className={'w-full h-full min-h-0 flex flex-col p-16 ' + (className || '')}>
                <div
                    className='w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium'
                    style={{ minHeight: minHeight }}
                >
                    {states.emptyTitle || 'No data available'}
                </div>
            </div>
        );
    }

    if (!hasPreparedRows) {
        return (
            <div className={'w-full h-full min-h-0 flex flex-col p-16 ' + (className || '')}>
                <div
                    className='w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium'
                    style={{ minHeight: minHeight }}
                >
                    {states.noResultsTitle || 'No matching results'}
                </div>
            </div>
        );
    }

    return (
        <div className={'flex h-full min-h-0 flex-col overflow-hidden ' + (className || '')}>

            <div className='min-h-0 flex-1 flex flex-col overflow-auto'>

                <table
                    className={'border-collapse ' + (hasHorizontalScroll ? 'table-auto' : 'w-full table-fixed')}
                    style={hasHorizontalScroll ? { minWidth: tableMinWidth } : undefined}
                >

                    <thead className='sticky top-0 z-10 bg-grey-50'>
                        <tr className='border-b border-grey-200'>
                            {columns.map((column) => {
                                const headerAlignClassName = ALIGN_CLASS_MAP[column.headerAlign || column.align || (column.numeric ? 'right' : 'left')] || ALIGN_CLASS_MAP.left;
                                const isActiveSortColumn = activeSort?.key === column.key;

                                return (
                                    <th
                                        key={column.key}
                                        onClick={() => handleSort(column)}
                                        className={
                                            'group border-b border-grey-200 px-16 py-16 text-xs font-medium tracking-wide ' +
                                            headerAlignClassName + ' ' +
                                            (column.sortable ? 'cursor-pointer select-none' : '') + ' ' +
                                            (column.className || '')
                                        }
                                        style={{ width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth }}
                                        title={column.sortable ? 'Sort' : undefined}
                                    >
                                        <div className={'flex items-center gap-6 ' + ((column.headerAlign || column.align || (column.numeric ? 'right' : 'left')) === 'right' ? 'justify-end' : (column.headerAlign || column.align) === 'center' ? 'justify-center' : 'justify-start')}>
                                            <span className='truncate'>{column.label}</span>

                                            {column.sortable ? (
                                                <span
                                                    className={
                                                        'flex shrink-0 items-center justify-center text-sm transition-all duration-200 ' +
                                                        (isActiveSortColumn ? '' : 'opacity-50')
                                                    }
                                                >
                                                    {isActiveSortColumn ? (
                                                        <PiArrowUpDuotone
                                                            className={
                                                                'transform transition-all duration-200 scale-110 ' +
                                                                (activeSort.direction === 'desc' ? 'rotate-180' : 'rotate-0')
                                                            }
                                                        />
                                                    ) : (
                                                        <PiArrowsDownUpDuotone className='transition-opacity duration-200 opacity-50 group-hover:opacity-100' />
                                                    )}
                                                </span>
                                            ) : null}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {preparedRows.map((row, index) => {
                            const resolvedRowKey =
                                typeof rowKey === 'function'
                                    ? rowKey(row, index)
                                    : (row?.[rowKey] ?? row?.key ?? row?.id ?? index);

                            return (
                                <tr
                                    key={resolvedRowKey}
                                    className={'border-b last:border-0 border-dashed border-grey-200 transition odd:bg-grey-50 ' + (index % 2 === 1 ? 'bg-grey-50' : '')}
                                >
                                    {columns.map((column) => {
                                        const alignClassName = ALIGN_CLASS_MAP[column.align || (column.numeric ? 'right' : 'left')] || ALIGN_CLASS_MAP.left;
                                        const editable = !column.readOnly;
                                        const value = row?.[column.key] ?? '';
                                        const cellError = errors?.[index]?.[column.key];
                                        const inputType = column.type === 'number' ? 'number' : 'text';

                                        return (
                                            <td
                                                key={column.key}
                                                className={
                                                    'px-16 py-16 text-xs text-grey-700 align-top ' +
                                                    alignClassName + ' ' +
                                                    (column.cellClassName || '')
                                                }
                                                style={{ width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth }}
                                            >
                                                {editable ? (
                                                    <div className='flex flex-col gap-6'>
                                                        <input
                                                            type={inputType}
                                                            step={column.type === 'number' ? 'any' : undefined}
                                                            value={value}
                                                            onChange={(e) => {
                                                                onChange?.(
                                                                    index,
                                                                    column.key,
                                                                    e.target.value,
                                                                    row
                                                                );
                                                            }}
                                                            className={
                                                                'w-full h-40 rounded-lg border bg-white px-10 text-xs outline-none transition duration-200 focus:ring-2 ring-black ' +
                                                                getInputAlignClass(column) + ' ' +
                                                                (cellError ? 'border-red-300' : 'border-grey-200')
                                                            }
                                                            placeholder={column.placeholder}
                                                            disabled={column.disabled === true}
                                                        />

                                                        {cellError ? (
                                                            <div className='px-2 text-[10px] text-red-600 leading-tight'>
                                                                {cellError}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : (
                                                    <div className={'min-h-36 flex items-center ' + alignClassName}>
                                                        {typeof column.renderCell === 'function'
                                                            ? column.renderCell({ row: row, column: column, rowIndex: index })
                                                            : (value === '' ? '-' : String(value))}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>

                </table>

            </div>

        </div>
    );
}